import React, { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';
import { ChatProvider } from './context/ChatContext';
// import { ChatButton, ChatPanel } from './components/Chat';

import AppWithSidebar from './components/AppWithSidebar';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Dashboard from './pages/Dashboard';
import ProblemRegister from './pages/ProblemRegister';
import TestsetPage from './pages/TestsetPage';
import PreReservationPage from './pages/PreReservationPage';
import ProblemEdit from './pages/ProblemEdit';
import TestRegister from './pages/TestRegister';
import TestResult from './pages/TestResult';
import ProblemPrint from './pages/ProblemPrint';
import TestPapers from './pages/TestPapers';
import StudentManagement from './pages/StudentManagement';
import ProblemSolution from './pages/ProblemSolution';
import SolutionRegister from './pages/SolutionRegister';
import TeacherManagement from './pages/TeacherManagement';
import ManagerManagement from './pages/ManagerManagement';
import UserManagement from './pages/UserManagement';
import ReservationManagement from './pages/ReservationManagement';
import StampManagement from './pages/StampManagement';
import ExamResult from './pages/ExamResult';
import ExamList from './pages/ExamList';
import TextbookPage from './pages/TextbookPage';
import Datapage from './pages/Datapage';
import TestPage from './pages/TestPage';
import './App.css';
import { PrintProvider } from './context/PrintContext';
import WrongAnswer from './pages/WrongAnswer';
import PresetPage from './pages/PresetPage';
import SchoolExamManagement from './pages/SchoolExamManagement';
import ProblemAgent from './pages/ProblemAgent';
import LearningStatus from './pages/LearningStatus';
import MathceptsTemp from './pages/MathceptsTemp';
import MathConcept from './pages/MathConcept';
import Statistics from './pages/statistics';
import AITracker from './pages/AITracker';
import KyTestPage from './pages/KyTestPage';
import StudentsHub from './pages/StudentsHub';
import ProblemHub from './pages/ProblemHub';
import ExamPapers from './pages/ExamPapers';
import OcrHub from './pages/OcrHub';
import Syllabus from './pages/Syllabus';
import StudentAdmin from './pages/StudentAdmin';
import MobileBottomNav from './components/MobileBottomNav';
import { V2_ROLES, OCR_ROLES, AI_RUN_ROLES, ACADEMY_ROLES } from './utils/roles';

const ProtectedRoute = React.memo(({ children }) => {
  const { getAccessToken, userType } = useAuth();
  const accessToken = getAccessToken();
  const location = useLocation();

  if (!accessToken) {
    return <Navigate to="/signin" replace />;
  }

  if (userType === 'teacher' &&
    (location.pathname.startsWith('/teachers') ||
      location.pathname.startsWith('/managers'))) {
    return <Navigate to="/dashboard" replace />;
  }

  // 매스컨셉 페이지는 manager 전용
  if (userType !== 'manager' && location.pathname.startsWith('/mathconcept')) {
    return <Navigate to="/dashboard" replace />;
  }

  // 학습(/students-hub)·문제(/problem-hub)·시험지(/exam-papers)·강의(/syllabi)는 매니저와 교사에게 연다.
  // 교사를 여는 이유: 공유받은 폴더·강의를 보려면 그 화면에 들어올 수 있어야 한다.
  // 허용 유형은 src/utils/roles.js 한 곳에서 열거한다 — 사이드바 게이팅과 짝이고,
  // 여기는 메뉴를 거치지 않은 직접 URL 접근을 막는 자리다.
  if (!V2_ROLES.includes(userType) &&
    (location.pathname.startsWith('/students-hub') || location.pathname.startsWith('/problem-hub') ||
      location.pathname.startsWith('/exam-papers') || location.pathname.startsWith('/syllabi'))) {
    return <Navigate to="/dashboard" replace />;
  }

  // 학생 v2(/academy)는 로컬 개발 또는 운영 전환 뒤 매니저 · 강사만 — 전환 전 운영 빌드는 메뉴(Sidebar)와 함께 막는다(src/utils/roles.js ACADEMY_ROLES).
  // 주소를 /student… 로 두지 않은 이유: 그 접두로 기존 「학생관리」 메뉴가 함께 켜진다(Sidebar 학생관리 isActive).
  if (!ACADEMY_ROLES.includes(userType) && location.pathname.startsWith('/academy')) {
    return <Navigate to="/dashboard" replace />;
  }

  // OCR(/ocr-hub)만 매니저 전용으로 남긴다 — 오너 결정(2026-09-09).
  if (!OCR_ROLES.includes(userType) && location.pathname.startsWith('/ocr-hub')) {
    return <Navigate to="/dashboard" replace />;
  }

  // AI 복사(변형) 배치 화면도 매니저만 — 오너 지시(2026-09-09). 목록의 [AI변형] 버튼을 감춘 것과 짝이다.
  // 대시보드가 아니라 **시험지 목록**으로 돌려보낸다 — 그 화면 자체는 교사에게 열려 있어,
  // 대시보드로 튕기면 방금 보던 자리를 잃는다.
  if (!AI_RUN_ROLES.includes(userType) && location.pathname.startsWith('/exam-papers/batch')) {
    return <Navigate to="/exam-papers" replace />;
  }

  return children;
});

// 모바일 네비게이션바를 App 레벨에서 한 번만 렌더링
// Layout/BypassLayout 각각에서 렌더링하면 페이지 이동 시 unmount/remount되어
// iOS Safari에서 viewport 재계산 문제로 하단 공백 발생
const MobileNavRenderer = React.memo(() => {
  const { getAccessToken } = useAuth();
  const location = useLocation();
  const [isMobileView, setIsMobileView] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const accessToken = getAccessToken();
  const isAuthPage = location.pathname === '/signin' || location.pathname === '/signup';

  if (!isMobileView || !accessToken || isAuthPage) return null;

  return <MobileBottomNav portalMode={true} />;
});

function AppContent() {
  useEffect(() => {
    const favicon = document.getElementById('favicon');
    const faviconPath = process.env.NODE_ENV === 'production'
      ? '/favicon_prod.ico'
      : '/favicon_dev.ico';

    if (favicon) {
      favicon.href = faviconPath;
    } else {
      const newFavicon = document.createElement('link');
      newFavicon.id = 'favicon';
      newFavicon.rel = 'icon';
      newFavicon.href = faviconPath;
      document.head.appendChild(newFavicon);
    }
  }, []);

  return (
    <>
      <PrintProvider>
        <AuthProvider>
          <ChatProvider>
            <SidebarProvider>
              <AppWithSidebar>
              <div className="App">
                <Routes>
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/problem-register"
                element={
                  <ProtectedRoute>
                    <ProblemRegister />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/testset"
                element={
                  <ProtectedRoute>
                    <TestsetPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pre-reservations"
                element={
                  <ProtectedRoute>
                    <PreReservationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/problem-edit"
                element={
                  <ProtectedRoute>
                    <ProblemEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/problem-edit/:page_number"
                element={
                  <ProtectedRoute>
                    <ProblemEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/test-register/:tree_id"
                element={
                  <ProtectedRoute>
                    <TestRegister />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/test-result/:exam_id"
                element={
                  <ProtectedRoute>
                    <TestResult />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/problem-print"
                element={
                  <ProtectedRoute>
                    <ProblemPrint />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/test-papers"
                element={
                  <ProtectedRoute>
                    <TestPapers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/test-papers/student/:student_id"
                element={
                  <ProtectedRoute>
                    <TestPapers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/:page_number?"
                element={
                  <ProtectedRoute>
                    <StudentManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/students/:student_id/test-papers"
                element={
                  <ProtectedRoute>
                    <TestPapers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/problem-solution"
                element={
                  <ProtectedRoute>
                    <Navigate to="/problem-solution/1" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/problem-solution/:page_number"
                element={
                  <ProtectedRoute>
                    <ProblemSolution />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/solution-register"
                element={
                  <ProtectedRoute>
                    <SolutionRegister />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teachers"
                element={
                  <ProtectedRoute>
                    <TeacherManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/managers"
                element={
                  <ProtectedRoute>
                    <ManagerManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-management"
                element={
                  <ProtectedRoute>
                    <UserManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reservations"
                element={
                  <ProtectedRoute>
                    <ReservationManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/:student_id/stamp"
                element={
                  <ProtectedRoute>
                    <StampManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/:student_id/kytest"
                element={
                  <ProtectedRoute>
                    <KyTestPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/:student_id/stamp/:stamp_id/result/:exam_id?"
                element={
                  <ProtectedRoute>
                    <ExamResult />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/:student_id/stamp/:stamp_id/wronganswer"
                element={<WrongAnswer />}
              />
              <Route
                path="/student/exam-list/:page_number?"
                element={
                  <ProtectedRoute>
                    <ExamList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/textbook"
                element={
                  <ProtectedRoute>
                    <TextbookPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/data"
                element={
                  <ProtectedRoute>
                    <Datapage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/data/:stamp_id"
                element={
                  <ProtectedRoute>
                    <Datapage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/statistics"
                element={
                  <ProtectedRoute>
                    <Statistics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/preset"
                element={
                  <ProtectedRoute>
                    <PresetPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/test"
                element={
                  <ProtectedRoute>
                    <TestPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/school-exams/:tab/:page_number"
                element={
                  <ProtectedRoute>
                    <SchoolExamManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/school-exams"
                element={<Navigate to="/school-exams/analysis/1" replace />}
              />
              <Route
                path="/problem-agent"
                element={
                  <ProtectedRoute>
                    <ProblemAgent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/problem-agent/page/:page_number"
                element={
                  <ProtectedRoute>
                    <ProblemAgent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/problem-agent/:work_id"
                element={
                  <ProtectedRoute>
                    <ProblemAgent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/learning-status"
                element={
                  <ProtectedRoute>
                    <LearningStatus />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/learning-status/student/:studentId"
                element={
                  <ProtectedRoute>
                    <LearningStatus />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/learning-status/student/:studentId/test/:testId"
                element={
                  <ProtectedRoute>
                    <LearningStatus />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/students-hub"
                element={
                  <ProtectedRoute>
                    <StudentsHub />
                  </ProtectedRoute>
                }
              />
              {/* 「전체 테스트」 — 학습 화면의 같은 자리에서 학생 표가 배정 표로 갈린다.
                  경로를 가르는 이유는 오갈 버튼을 두지 않기 위해서다(오너 결정 2026-09-06):
                  들어가는 길은 학생 목록 위의 [전체 테스트] 하나뿐이고, 나오는 길은
                  브라우저 뒤로가기와 사이드바의 「학습」이다. */}
              <Route
                path="/students-hub/tests"
                element={
                  <ProtectedRoute>
                    <StudentsHub view="tests" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/students-hub/student/:studentId"
                element={
                  <ProtectedRoute>
                    <StudentsHub />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/problem-hub"
                element={
                  <ProtectedRoute>
                    <ProblemHub />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/exam-papers"
                element={
                  <ProtectedRoute>
                    <ExamPapers />
                  </ProtectedRoute>
                }
              />
              {/* 시험지 하위 화면 — 새 시험지·상세·배포본·AI 복사 배치. 같은 셸이 뷰만 갈아 끼운다 */}
              {['/exam-papers/new', '/exam-papers/paper/:paperId', '/exam-papers/assignment/:assignmentId', '/exam-papers/batch/:batchId'].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute>
                      <ExamPapers />
                    </ProtectedRoute>
                  }
                />
              ))}
              {/* 학생 v2 — 왼쪽 학생 표 + 오른쪽(학생을 고르지 않으면 통계 페이지, 고르면 학생 상세). 같은 셸이 주소로 갈아 끼운다
                  설계: docs/student-admin-v2/1010_SPEC_student-admin-v2-screen.html */}
              {['/academy', '/academy/activity', '/academy/student/:studentId', '/academy/stats/:report'].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute>
                      <StudentAdmin />
                    </ProtectedRoute>
                  }
                />
              ))}
              {/* 강의 — 목록과 한 강의(나무+편집). 같은 페이지가 주소의 강의 번호로 갈아 끼운다 */}
              {['/syllabi', '/syllabi/:syllabusId'].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute>
                      <Syllabus />
                    </ProtectedRoute>
                  }
                />
              ))}
              <Route
                path="/problem-hub/problem/:problemId"
                element={
                  <ProtectedRoute>
                    <ProblemHub />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/problem-hub/work/:workId"
                element={
                  <ProtectedRoute>
                    <ProblemHub />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ocr-hub"
                element={
                  <ProtectedRoute>
                    <OcrHub />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ocr-hub/project/:projectId"
                element={
                  <ProtectedRoute>
                    <OcrHub />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mathcepts-temp"
                element={
                  <ProtectedRoute>
                    <MathceptsTemp />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mathconcept"
                element={
                  <ProtectedRoute>
                    <MathConcept />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-tracker"
                element={
                  <ProtectedRoute>
                    <AITracker />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/signin" replace />} />
            </Routes>
          </div>
              </AppWithSidebar>
              {/* <ChatButton /> */}
              {/* <ChatPanel /> */}
              <MobileNavRenderer />
            </SidebarProvider>
          </ChatProvider>
        </AuthProvider>
      </PrintProvider>
    </>
  );
}

// 기존 Routes·권한·레이아웃을 유지하면서 SPA의 push/replace/뒤로 가기를 useBlocker로 함께 보호한다.
// Router는 렌더마다 다시 만들지 않는다(초안과 브라우저 이력을 유지).
const router = createBrowserRouter([{ path: '*', element: <AppContent /> }]);

export default function App() {
  return <RouterProvider router={router} />;
}
